import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { gql, useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Image,
  Input,
  Layout,
  Menu,
  notification,
  PageHeader,
  Popconfirm,
  Row,
  Space,
  Table,
  Tag,
  Tooltip,
} from "antd";
import { useApplicationContext } from "components/Application";
import React, { Fragment, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";

const collectionLinesQuery = gql`
  query ($pagination: ConnectionPagination, $searchQuery: String) {
    collectionLines(pagination: $pagination, searchQuery: $searchQuery) {
      nodes {
        id
        code
        nameIt
        nameEn
        position
        color
        coverHorizontal
        brand {
          id
          code
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        page
        size
      }
      totalCount
      totalPages
    }
  }
`;

const deleteCollectionLineMutation = gql`
  mutation ($id: ID!) {
    deleteCollectionLine(id: $id) {
      line {
        id
        nameEn
      }
    }
  }
`;

export const List: React.FC<{}> = () => {
  const {
    siteEnvironment: { appEndpointStore },
  } = useApplicationContext();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const navigate = useNavigate();

  const variables = {
    pagination: { page: 1, pageSize: 0 },
    orderBy: { direction: "ASC", field: "ID" },
    searchQuery,
  };
  const { loading, data, refetch } = useQuery(collectionLinesQuery, { variables, fetchPolicy: "cache-and-network" });

  const handleSearch = (value: string) => setSearchQuery(value);
  const handleSearchCancel = () => setSearchQuery("");
  const handleTableChange = () => {};

  const [execDelete] = useMutation(deleteCollectionLineMutation, {
    errorPolicy: "none",
    onError: ({ graphQLErrors }) => {
      notification.error({
        placement: "bottomRight",
        message: "Cannot delete this Line",
      });
    },
    onCompleted: ({ deleteCollectionLine: { line } }) => {
      notification.success({
        placement: "bottomRight",
        message: `Line "${line.nameEn}" deleted`,
      });
      refetch();
    },
  });

  const columns = [
    {
      title: "Name",
      dataIndex: "nameIt",
      render: (value: string, resource: any) => <NavLink to={resource.id}>{value}</NavLink>,
    },
    {
      title: "Brand Name",
      dataIndex: "brand",
      key: "userBrands.brand",
      render: (value: string, resource: any) =>
        resource.brand.code === "palto" ? (
          <Tag>{resource.brand.code.toUpperCase()}</Tag>
        ) : (
          <Tag color="blue">{resource.brand.code.toUpperCase()}</Tag>
        ),
    },
    {
      title: "Color",
      dataIndex: "color",
    },
    {
      title: "Cover",
      dataIndex: "coverHorizontal",
      render: (value: string) => <Image src={`${appEndpointStore}${value}`} width={100} />,
    },
    {
      width: "80px",
      title: "Actions",
      dataIndex: "id",
      render: (id: string, resource: any) => (
        <Row justify="end">
          <Space>
            <Tooltip title="Edit" placement="bottom">
              <Button onClick={() => navigate(`${id}/edit`)} icon={<EditOutlined />}></Button>
            </Tooltip>
            <Popconfirm title="Are you sure?" icon={null} onConfirm={() => execDelete({ variables: { id } })}>
              <Tooltip title="Delete" placement="bottom">
                <Button danger icon={<DeleteOutlined />}></Button>
              </Tooltip>
            </Popconfirm>
          </Space>
        </Row>
      ),
    },
  ];

  return (
    <Fragment>
      <Layout.Header className="header-background">
        <Row justify="space-between">
          <Menu theme="light" mode="horizontal">
            <Input.Search
              placeholder="search"
              onSearch={handleSearch}
              key={variables.searchQuery ?? "__search__"}
              defaultValue={variables.searchQuery ?? ""}
              style={{ width: 500, margin: "16px 30px" }}
            />
            <Button disabled={searchQuery.length === 0} onClick={handleSearchCancel} style={{ margin: "16px 0" }}>
              Cancel
            </Button>
          </Menu>
        </Row>
      </Layout.Header>
      <Layout className="content">
        <PageHeader
          ghost={true}
          title="Lines"
          extra={
            <Button
              onClick={() => {
                navigate("new");
              }}
              type="primary"
            >
              <PlusOutlined />
              New
            </Button>
          }
        />
        <Layout.Content className="content">
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={data?.collectionLines.nodes}
            pagination={{
              hideOnSinglePage: true,
              showSizeChanger: true,
              showTotal: (total, range) => `Showing ${range} of ${total}`,
              total: data?.totalCount ?? 0,
            }}
            onChange={handleTableChange}
          />
        </Layout.Content>
      </Layout>
    </Fragment>
  );
};
